import {
  REMARK_KINDS,
  type RemarkSummary,
  VISIT_ACTIONS,
  VISIT_KINDS,
  VISIT_STATUSES,
  type VisitDetail,
  type VisitKind,
  type VisitSummary,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, todayLocal } from '../../lib/format';
import { counterpartiesQuery, projectQuery } from '../../lib/projects';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel } from '../../ui/layout';
import { RemarkBadge, useProjectDefault, useProjectsWith } from '../psd/shared';
import { NewRemarkDialog } from './SupervisionPage';

/**
 * Журнал посещений авторского и технического надзора (АВ-01, АВ-02): список посещений во вкладке «Журнал
 * посещений» раздела «Надзор», карточка посещения с записью, выданными замечаниями и проверкой открытых
 * замечаний прошлых визитов.
 */

export function VisitsList({ projectId }: { projectId?: string }) {
  const q = useQuery({
    queryKey: ['visits', projectId],
    queryFn: () => api<VisitSummary[]>('/supervision/visits', { query: { projectId } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data.length)
    return (
      <EmptyState title="Посещений пока нет">
        Каждый выезд авторского или технического надзора — запись журнала: кто был, что осмотрено, какие
        замечания выданы и что проверено с прошлого раза.
      </EmptyState>
    );
  return (
    <ul className="flex flex-col gap-2">
      {q.data.map((v) => (
        <li key={v.id}>
          <Link
            to="/supervision/visits/$visitId"
            params={{ visitId: v.id }}
            className="flex flex-col gap-1.5 rounded-lg border border-line bg-surface p-4 hover:border-ink-3 md:flex-row md:items-start md:gap-4"
          >
            <span className="num shrink-0 font-medium text-ink md:w-24">{v.number}</span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-ink">
                {VISIT_KINDS[v.kind]}, {date(v.visitDate)}
              </span>
              <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                {v.projectCode}
                {v.objectName ? ` · ${v.objectName}` : ''}
                {v.organizationName ? ` · ${v.organizationName}` : ''}
                {v.participants.length ? ` · ${v.participants.join(', ')}` : ''}
              </span>
            </span>
            <span className="flex flex-wrap items-center gap-1.5 md:justify-end">
              <Badge tone={v.status === 'open' ? 'info' : undefined}>{VISIT_STATUSES[v.status]}</Badge>
              <span className="text-[13px] text-ink-2">
                выдано {v.issued} · проверено {v.checked}
              </span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

type People = Array<{ id: string; fullName: string; external: boolean }>;

export function NewVisitDialog({
  projectId,
  edit,
  onClose,
}: {
  projectId?: string;
  /** Изменение записи: посещение с карточки */
  edit?: VisitDetail;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { list } = useProjectsWith('supervision.issue');
  const [f, setF] = useState({
    projectId:
      edit?.projectId ??
      (projectId && list.some((p) => p.id === projectId) ? projectId : (list[0]?.id ?? '')),
    kind: (edit?.kind ?? 'author_supervision') as VisitKind,
    visitDate: edit?.visitDate ?? todayLocal(),
    objectId: edit?.objectId ?? '',
    organizationId: edit?.organizationId ?? '',
    participantIds: edit?.participantIds ?? ([] as string[]),
    purpose: edit?.purpose ?? '',
    notes: edit?.notes ?? '',
  });
  const setProject = useCallback((id: string) => setF((x) => ({ ...x, projectId: id })), []);
  useProjectDefault(list, edit?.projectId ?? projectId, f.projectId, setProject);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const project = useQuery({ ...projectQuery(f.projectId), enabled: Boolean(f.projectId) });
  const orgs = useQuery({ ...counterpartiesQuery(), retry: false });
  const people = useQuery({
    queryKey: ['supervision-people', f.projectId, 'visit'],
    queryFn: async () => {
      const [a, b] = await Promise.all(
        (['issue', 'view'] as const).map((perm) =>
          api<People>('/supervision/people', { query: { projectId: f.projectId, perm } }),
        ),
      );
      const m = new Map<string, People[number]>();
      for (const p of [...a!, ...b!]) m.set(p.id, p);
      return [...m.values()].sort((x, y) => x.fullName.localeCompare(y.fullName, 'ru'));
    },
    enabled: Boolean(f.projectId),
  });
  const m = useMutation({
    mutationFn: () => {
      const body = {
        visitDate: f.visitDate,
        objectId: f.objectId || null,
        organizationId: f.organizationId || null,
        participantIds: f.participantIds,
        purpose: f.purpose.trim() || null,
        notes: f.notes.trim() || null,
      };
      if (!edit)
        return api<VisitDetail>('/supervision/visits', {
          body: { ...body, projectId: f.projectId, kind: f.kind },
        });
      // В изменении — только то, что поменяли: текст со скрытыми суммами не уходит обратно маской
      const was: Record<string, unknown> = {
        visitDate: edit.visitDate,
        objectId: edit.objectId,
        organizationId: edit.organizationId,
        participantIds: edit.participantIds,
        purpose: edit.purpose,
        notes: edit.notes,
      };
      const patch = Object.fromEntries(
        Object.entries(body).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(was[k] ?? null)),
      );
      return api<VisitDetail>(`/supervision/visits/${edit.id}`, { method: 'PATCH', body: patch });
    },
    onSuccess: (v) => {
      qc.invalidateQueries({ queryKey: ['visits'] });
      qc.invalidateQueries({ queryKey: ['visit'] });
      toast(edit ? `Запись ${v.number} изменена` : `Посещение ${v.number} записано`);
      onClose();
      if (!edit) navigate({ to: '/supervision/visits/$visitId', params: { visitId: v.id } });
    },
  });
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(v) => !v && onClose()}
      title={edit ? `Изменить запись ${edit.number}` : 'Новое посещение'}
      description="Замечания и указания выдаются в карточке посещения — они попадут в эту запись журнала."
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!f.projectId || !f.visitDate}
            onClick={() => m.mutate()}
          >
            {edit ? 'Сохранить' : 'Записать посещение'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Проект" className="sm:col-span-2">
          <Select
            className="h-11 md:h-10"
            disabled={Boolean(edit)}
            value={f.projectId}
            onChange={(e) =>
              setF((x) => ({ ...x, projectId: e.target.value, objectId: '', participantIds: [] }))
            }
          >
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Надзор">
          <Select
            className="h-11 md:h-10"
            disabled={Boolean(edit)}
            value={f.kind}
            onChange={(e) => set('kind', e.target.value as VisitKind)}
          >
            {(Object.keys(VISIT_KINDS) as VisitKind[]).map((k) => (
              <option key={k} value={k}>
                {VISIT_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Дата посещения">
          <Input
            type="date"
            className="h-11 md:h-10"
            value={f.visitDate}
            onChange={(e) => set('visitDate', e.target.value)}
          />
        </Field>
        <Field label="Объект">
          <Select
            className="h-11 md:h-10"
            value={f.objectId}
            onChange={(e) => set('objectId', e.target.value)}
          >
            <option value="">—</option>
            {(project.data?.objects ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.code ? `${o.code} ` : ''}
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Организация" hint="Проектировщик или технадзор">
          <Select
            className="h-11 md:h-10"
            value={f.organizationId}
            onChange={(e) => set('organizationId', e.target.value)}
          >
            <option value="">—</option>
            {(orgs.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.shortName || c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Цель посещения" className="sm:col-span-2">
          <Input
            className="h-11 md:h-10"
            value={f.purpose}
            onChange={(e) => set('purpose', e.target.value)}
            placeholder="Освидетельствование армирования фундамента КНС-2"
          />
        </Field>
        <div className="sm:col-span-2">
          <div className="mb-1.5 text-[13px] text-ink-2">Кто был на объекте</div>
          {people.data?.length ? (
            <div className="grid max-h-48 gap-1 overflow-auto rounded-md border border-line p-2 sm:grid-cols-2">
              {people.data.map((p) => (
                <Checkbox
                  key={p.id}
                  className="min-h-11 items-center md:min-h-0"
                  checked={f.participantIds.includes(p.id)}
                  onChange={(e) =>
                    set(
                      'participantIds',
                      e.target.checked
                        ? [...f.participantIds, p.id]
                        : f.participantIds.filter((x) => x !== p.id),
                    )
                  }
                  label={`${p.fullName}${p.external ? ' (внешний)' : ''}`}
                />
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-ink-2">Участники проекта с правом на надзор появятся здесь.</p>
          )}
        </div>
        <Field label="Запись в журнале" hint="Что осмотрено, что установлено" className="sm:col-span-2">
          <Textarea rows={5} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
      </div>
      <ErrorBox error={m.error} className="mt-4" />
    </Dialog>
  );
}

function RemarkRows({ rows, visitId, check }: { rows: RemarkSummary[]; visitId: string; check?: boolean }) {
  return (
    <ul className="flex flex-col divide-y divide-line">
      {rows.map((r) => (
        <li key={r.id}>
          <Link
            to="/supervision/remarks/$remarkId"
            params={{ remarkId: r.id }}
            search={check ? { visit: visitId } : { visit: undefined }}
            className="flex min-h-11 flex-col gap-1 py-2.5 hover:underline md:flex-row md:items-center md:gap-3"
          >
            <span className="num shrink-0 font-medium md:w-24">{r.number}</span>
            <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
              {REMARK_KINDS[r.kind]}: {r.title}
            </span>
            <span className="flex flex-wrap items-center gap-1.5">
              <RemarkBadge status={r.status} overdue={r.overdue} />
              <span className="text-[13px] text-ink-2">до {date(r.dueDate)}</span>
              {check ? <span className="text-[13px] font-medium text-accent-text">Проверить →</span> : null}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function VisitPage() {
  const { visitId } = useParams({ strict: false }) as { visitId: string };
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['visit', visitId],
    queryFn: () => api<VisitDetail>(`/supervision/visits/${visitId}`),
  });
  const [remark, setRemark] = useState(false);
  const [edit, setEdit] = useState(false);
  useCrumbs(
    q.data
      ? [
          { label: q.data.projectCode, to: `/supervision?project=${q.data.projectId}&tab=visits` },
          { label: 'Журнал посещений', to: `/supervision?tab=visits&project=${q.data.projectId}` },
          { label: q.data.number },
        ]
      : null,
    q.error,
  );
  const act = useMutation({
    mutationFn: (action: 'close' | 'reopen') =>
      api<VisitDetail>(`/supervision/visits/${visitId}/action`, { body: { action } }),
    onSuccess: (v) => {
      qc.invalidateQueries({ queryKey: ['visit', visitId] });
      qc.invalidateQueries({ queryKey: ['visits'] });
      toast(v.status === 'closed' ? `Запись ${v.number} подписана` : `Запись ${v.number} открыта`);
    },
  });
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const v = q.data;
  return (
    <Page>
      <PageHeader
        title={`${VISIT_KINDS[v.kind]} ${v.number}`}
        subtitle={`${date(v.visitDate)} · ${v.projectCode}${v.objectName ? ` · ${v.objectName}` : ''}`}
        actions={
          <>
            {v.canIssue ? (
              <Button
                variant="primary"
                size="lg"
                icon={<Plus className="size-4" />}
                onClick={() => setRemark(true)}
              >
                Добавить замечание
              </Button>
            ) : null}
            {v.actions.includes('close') ? (
              <Button
                size="lg"
                variant="secondary"
                loading={act.isPending}
                onClick={() => act.mutate('close')}
              >
                {VISIT_ACTIONS.close}
              </Button>
            ) : null}
            {v.actions.includes('edit') ? (
              <Button size="lg" variant="ghost" onClick={() => setEdit(true)}>
                {VISIT_ACTIONS.edit}
              </Button>
            ) : null}
            {v.actions.includes('reopen') ? (
              <Button size="lg" variant="ghost" loading={act.isPending} onClick={() => act.mutate('reopen')}>
                {VISIT_ACTIONS.reopen}
              </Button>
            ) : null}
          </>
        }
      >
        <div className="mt-2">
          <Badge tone={v.status === 'open' ? 'info' : undefined}>{VISIT_STATUSES[v.status]}</Badge>
        </div>
      </PageHeader>
      <ErrorBox error={act.error} className="mb-4" />
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <div className="flex min-w-0 flex-col gap-4">
          <Panel title="Запись журнала">
            <KeyValue
              items={[
                { label: 'Организация', value: v.organizationName },
                { label: 'Кто был', value: v.participants.length ? v.participants.join(', ') : null },
                { label: 'Цель', value: v.purpose },
                {
                  label: 'Записано',
                  value: v.notes ? <span className="whitespace-pre-wrap">{v.notes}</span> : null,
                },
                { label: 'Записал', value: v.createdBy },
                {
                  label: 'Подписано',
                  value: v.closedAt ? `${v.closedBy ?? '—'}, ${dateTime(v.closedAt)}` : null,
                },
              ]}
            />
          </Panel>
          <Panel title="Выдано на посещении" meta={String(v.remarks.length)}>
            {v.remarks.length ? (
              <RemarkRows rows={v.remarks} visitId={v.id} />
            ) : (
              <p className="text-[14px] text-ink-2">
                {v.canIssue ? 'Замечания и указания — кнопкой «Добавить замечание».' : 'Не выдавалось.'}
              </p>
            )}
          </Panel>
          {v.toCheck.length ? (
            <Panel title="К проверке с прошлых посещений" meta={String(v.toCheck.length)}>
              <p className="mb-2 text-[13px] text-ink-2">
                {v.canIssue
                  ? 'Откройте замечание и закройте его или верните на устранение — отметка попадёт в эту запись.'
                  : 'Открытые замечания этого надзора по проекту.'}
              </p>
              <RemarkRows rows={v.toCheck} visitId={v.id} check={v.canIssue} />
            </Panel>
          ) : null}
        </div>
        <Panel title="Проверено на посещении" meta={String(v.checkedItems.length)}>
          {v.checkedItems.length ? (
            <ul className="flex flex-col gap-3">
              {v.checkedItems.map((c) => (
                <li key={`${c.remarkId}-${c.at}`} className="text-[14px]">
                  <Link
                    to="/supervision/remarks/$remarkId"
                    params={{ remarkId: c.remarkId }}
                    search={{ visit: undefined }}
                    className="inline-flex min-h-11 items-center font-medium underline md:min-h-0"
                  >
                    {c.number}
                  </Link>{' '}
                  — {c.action.toLowerCase()}
                  <div className="text-ink-2 [overflow-wrap:anywhere]">{c.title}</div>
                  {c.comment ? (
                    <div className="text-ink-2 [overflow-wrap:anywhere]">«{c.comment}»</div>
                  ) : null}
                  <div className="text-[13px] text-ink-3">
                    {c.by ?? '—'}, {dateTime(c.at)}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[14px] text-ink-2">Пока ничего.</p>
          )}
        </Panel>
      </div>
      {remark ? (
        <NewRemarkDialog
          projectId={v.projectId}
          visit={{ id: v.id, kind: v.kind, number: v.number }}
          onClose={() => setRemark(false)}
        />
      ) : null}
      {edit ? <NewVisitDialog edit={v} onClose={() => setEdit(false)} /> : null}
    </Page>
  );
}
